import React from 'react';
import PropTypes from 'prop-types';
import './Switch.scss';

const Switch = ({
  checked = false,
  onChange,
  label,
  disabled = false,
  size = 'medium',
  variant = 'skeuomorphic',
  color = 'primary',
  id,
  className = '',
  tooltip,
  animated = true,
  icon,
  metallic = false,
  gloss = true,
  embossed = true
}) => {
  const switchId = id || `switch-${Math.random().toString(36).substr(2, 9)}`;

  const handleChange = (event) => {
    if (!disabled && onChange) {
      onChange(event.target.checked, event);
    }
  };

  const handleKeyPress = (event) => {
    if (!disabled && (event.key === ' ' || event.key === 'Enter')) {
      event.preventDefault();
      if (onChange) {
        onChange(!checked, event);
      }
    }
  };

  const containerClasses = [
    'skeuomorphic-switch-container',
    `switch-${size}`,
    `switch-${variant}`,
    `switch-${color}`,
    disabled ? 'switch-disabled' : '',
    checked ? 'switch-checked' : 'switch-unchecked',
    animated ? 'switch-animated' : '',
    metallic ? 'switch-metallic' : '',
    gloss ? 'switch-gloss' : '',
    embossed ? 'switch-embossed' : '',
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClasses} title={tooltip}>
      <input
        type="checkbox"
        id={switchId}
        className="switch-input"
        checked={checked}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        disabled={disabled}
        aria-label={label || `Toggle switch ${checked ? 'on' : 'off'}`}
        aria-describedby={tooltip ? `${switchId}-tooltip` : undefined}
      />

      <label htmlFor={switchId} className="switch-label">
        {/* Track avec effets skeuomorphic */}
        <div className="switch-track">
          {/* Couche de fond avec texture */}
          <div className="switch-track-base">
            <div className="switch-track-texture"></div>
            <div className="switch-track-shadow-inner"></div>
          </div>

          {/* LED indicators */}
          <div className="switch-indicators">
            <div className="switch-led switch-led-off"></div>
            <div className="switch-led switch-led-on"></div>
          </div>

          {/* Thumb avec effets 3D réalistes */}
          <div className="switch-thumb">
            <div className="switch-thumb-base">
              <div className="switch-thumb-surface">
                {icon && (
                  <span className="switch-icon">
                    {typeof icon === 'function' ? icon(checked) : icon}
                  </span>
                )}
                <div className="switch-thumb-highlight"></div>
                <div className="switch-thumb-texture"></div>
              </div>
              <div className="switch-thumb-shadow"></div>
              <div className="switch-thumb-ridge"></div>
            </div>
          </div>

          {/* Effets de reflet et brillance */}
          <div className="switch-glass-overlay">
            <div className="switch-reflection"></div>
            <div className="switch-glare"></div>
          </div>
        </div>
      </label>

      {/* Label externe classique */}
      {label && (
        <label htmlFor={switchId} className="switch-text">
          {label}
        </label>
      )}
    </div>
  );
};

Switch.propTypes = {
  checked: PropTypes.bool,
  onChange: PropTypes.func,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['skeuomorphic', 'metallic', 'glass', 'mechanical']),
  color: PropTypes.oneOf(['primary', 'secondary', 'success', 'warning', 'error', 'info']),
  id: PropTypes.string,
  className: PropTypes.string,
  tooltip: PropTypes.string,
  animated: PropTypes.bool,
  icon: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  metallic: PropTypes.bool,
  gloss: PropTypes.bool,
  embossed: PropTypes.bool
};

export default Switch;